import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * ModuleRegistry — client-side counterpart to backend module framework.
 * Fetches installed modules and their manifests, exposes them to the shell,
 * and mounts pages/widgets/nav via `frontendModules` map.
 */
const ModuleCtx = createContext({
  catalog: [],
  installed: [],
  installedIds: [],
  loading: true,
  refresh: async () => {},
  install: async () => {},
  uninstall: async () => {},
});

// Registered frontend module descriptors (page + widget components + settings UI)
// See /app/frontend/src/modules/index.js
export const frontendModules = {};

export function registerModule(id, descriptor) {
  frontendModules[id] = descriptor;
}

export function ModuleProvider({ children }) {
  const { user, workspace } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [installed, setInstalled] = useState([]);
  const [installedIds, setInstalledIds] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [c, ins] = await Promise.all([
        api.get("/modules/catalog"),
        api.get("/modules/installed"),
      ]);
      setCatalog(c.data.modules || []);
      setInstalled(ins.data.modules || []);
      setInstalledIds(ins.data.installed || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  // Refresh when the authenticated user / workspace becomes available or
  // changes. Previously `refresh()` ran once at app boot (before login) and
  // returned 401 for /modules/installed, leaving the sidebar without POS /
  // Sales / Customers / Inventory until a full page reload.
  useEffect(() => {
    refresh();
  }, [refresh, user?.user_id, workspace?.workspace_id]);

  async function install(ids) {
    await api.post("/modules/install", { modules: Array.isArray(ids) ? ids : [ids] });
    await refresh();
  }
  async function uninstall(ids) {
    await api.post("/modules/uninstall", { modules: Array.isArray(ids) ? ids : [ids] });
    await refresh();
  }

  return (
    <ModuleCtx.Provider value={{ catalog, installed, installedIds, loading, refresh, install, uninstall }}>
      {children}
    </ModuleCtx.Provider>
  );
}

export const useModules = () => useContext(ModuleCtx);

export function useModule(id) {
  const { catalog, installedIds } = useModules();
  const manifest = catalog.find((m) => m.id === id);
  return { manifest, installed: installedIds.includes(id) };
}
